/* eslint-disable eslint-comments/no-restricted-disable */
/* eslint-disable @atlassian/react-entrypoint/no-imports-in-entrypoint-file */
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { isProjectsInJiraM2Enabled } from '@jira/native-integration-projects/src/index.tsx';
import { TEAM_MANAGED_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import { getProjectKey } from '@atlassian/jira-router-resources-utils/src/utils/get-project-key-id/index.tsx';

import { IdentityUserAri } from '@atlassian/ari/identity/user';

import { AnyAri } from '@atlassian/ari/any-ari';
import subspaceMenuListQuery from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-projects-subspaces/src/__generated__/SubspaceMenuListQuery.graphql';
import parameters from './__generated__/JswExpandableProjectQuery.graphql';
import refactoredParameters from './__generated__/JswExpandableProjectRefactoredQuery.graphql';

const NO_OF_BOARDS_TO_FETCH = 25;

export const nav4JSWProjectEntryPoint = createEntryPoint({
	root: JSResourceForUserVisible(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(): Promise<any> => {
			if (isProjectsInJiraM2Enabled()) {
				return import(
					/* webpackChunkName: "nav4-jsw-project-entry-point-subspace" */ './JswExpandableProjectSubspaceQuery'
				);
			}
			// Feature gate to choose between original and refactored implementation
			if (expVal('navx-1453-jsw-cmp-boards-dnd-2', 'isEnabled', false)) {
				return import(
					/* webpackChunkName: "nav4-jsw-project-entry-point-refactored" */ './JswExpandableProjectRefactoredQuery'
				);
			}
			return import(
				/* webpackChunkName: "nav4-jsw-project-entry-point" */ './JswExpandableProjectQuery'
			);
		},
		{ includeInShellOnlySsrBundle: true, entryFsSsrLiftImportToModule: true },
	),
	getPreloadProps: (params: EntryPointRouteParams) => {
		const cloudId = params.tenantContext?.cloudId ?? '';

		let projectKey = '';
		if (params.context?.match.params?.projectKey) {
			projectKey = params.context.match.params.projectKey;
		} else if (params.context?.match.params?.issueKey && fg('move-issue-view-to-entrypoint')) {
			projectKey = getProjectKey(params.context.match, params.context.query);
		}

		// Feature gate to choose between original and refactored GraphQL parameters
		let queryParameters = expVal('navx-1453-jsw-cmp-boards-dnd-2', 'isEnabled', false)
			? refactoredParameters
			: parameters;
		const projectStyle = params.context?.match.params?.projectStyle;
		if (projectStyle === TEAM_MANAGED_PROJECT && isProjectsInJiraM2Enabled()) {
			queryParameters = subspaceMenuListQuery;
		}

		const entityAri = AnyAri.create({
			cloudId,
			resourceType: 'project',
			resourceId: projectKey,
			resourceOwner: 'jira',
		}).toString();
		const ownerAri = IdentityUserAri.create({
			userId: params.tenantContext.atlassianAccountId || 'UNKNOWN',
		}).toString();

		// Feature gate variables - original query doesn't have entityAri and ownerAri
		const variables = expVal('navx-1453-jsw-cmp-boards-dnd-2', 'isEnabled', false)
			? {
					cloudId,
					projectKey,
					noOfBoardsToFetch: NO_OF_BOARDS_TO_FETCH,
					entityAri,
					ownerAri,
				}
			: {
					cloudId,
					projectKey,
					noOfBoardsToFetch: NO_OF_BOARDS_TO_FETCH,
				};

		return {
			queries: {
				projects: {
					parameters: queryParameters,
					variables,
				},
			},
			extraProps: {
				projectStyle,
			},
		};
	},
});
